/**
 * Lead capture helpers — shared between photo-to-art and instant-preview.
 *
 * Validation + country picker live here so both surfaces ship the same
 * email/phone hygiene to Klaviyo + Supabase. Identification itself goes
 * through window.instameIdentify (snippets/user-identity.liquid) — this
 * module never calls Klaviyo or Supabase directly.
 *
 * Phone country list is intentionally narrow (US/CA/MX) — extending it
 * means updating both the country picker UI and the per-country digit
 * regex in isValidPhone.
 */
(function () {
  "use strict";

  if (window.LeadCapture) return;

  function isValidEmail(value) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value || "");
  }

  // ── Typo correction (mailcheck-style, no dependency) ────────────────
  // The single biggest preventable source of bounces is a mistyped
  // domain: gnail.com, gmail.con, gmail.comd, hotmil.cim, myyahoo.com.
  // suggestEmail() returns a corrected full address (preserving the
  // user-typed local part) when the domain is a near-miss of a popular
  // one, or null when the address already looks fine / is unrecognizable.
  // It SUGGESTS only — callers must never auto-replace or block on it,
  // so a rare-but-real domain is never rejected.
  var POPULAR_DOMAINS = [
    "gmail.com", "yahoo.com", "hotmail.com", "outlook.com", "icloud.com",
    "aol.com", "comcast.net", "msn.com", "live.com", "me.com", "mac.com",
    "verizon.net", "att.net", "sbcglobal.net", "ymail.com", "proton.me",
    "protonmail.com", "googlemail.com", "gmx.com", "cox.net", "charter.net",
    "bellsouth.net", "hotmail.co.uk", "yahoo.co.uk",
  ];
  var POPULAR_SLDS = [
    "gmail", "yahoo", "hotmail", "outlook", "icloud", "aol", "comcast",
    "msn", "live", "me", "mac", "verizon", "att", "sbcglobal", "ymail",
    "proton", "protonmail", "googlemail", "gmx", "cox", "charter", "bellsouth",
  ];
  var POPULAR_TLDS = ["com", "net", "org", "edu", "co", "us", "ca", "io", "info"];

  // Standard iterative Levenshtein (two-row). Small inputs, called at most
  // a couple times per keystroke — no need for anything fancier.
  function editDistance(a, b) {
    a = a || "";
    b = b || "";
    var m = a.length;
    var n = b.length;
    if (!m) return n;
    if (!n) return m;
    var prev = new Array(n + 1);
    var cur = new Array(n + 1);
    for (var j = 0; j <= n; j++) prev[j] = j;
    for (var i = 1; i <= m; i++) {
      cur[0] = i;
      for (var k = 1; k <= n; k++) {
        var cost = a.charCodeAt(i - 1) === b.charCodeAt(k - 1) ? 0 : 1;
        cur[k] = Math.min(prev[k] + 1, cur[k - 1] + 1, prev[k - 1] + cost);
      }
      var tmp = prev;
      prev = cur;
      cur = tmp;
    }
    return prev[n];
  }

  // Closest entry in `list` within `maxDist`. Returns null on an exact
  // match (nothing to suggest) or when nothing is close enough.
  function closest(target, list, maxDist) {
    var best = null;
    var bestDist = Infinity;
    for (var i = 0; i < list.length; i++) {
      if (list[i] === target) return null;
      var d = editDistance(target, list[i]);
      if (d < bestDist) {
        bestDist = d;
        best = list[i];
      }
    }
    return bestDist <= maxDist ? best : null;
  }

  function suggestEmail(value) {
    var raw = (value || "").trim();
    var at = raw.lastIndexOf("@");
    if (at < 1 || at === raw.length - 1) return null;
    var local = raw.slice(0, at);
    var domain = raw.slice(at + 1).toLowerCase();
    if (domain.indexOf(".") === -1) return null; // no TLD typed yet
    if (POPULAR_DOMAINS.indexOf(domain) !== -1) return null; // already good

    // 1) Whole-domain near-miss: gnail.com, gmal.com, hotmil.cim, myyahoo.com.
    //    Tolerance scales with length so a 5-char domain (g.com) can't match
    //    a popular one half its characters away (would-be "me.com").
    var maxWholeDist = domain.length <= 6 ? 1 : 2;
    var domainHit = closest(domain, POPULAR_DOMAINS, maxWholeDist);
    if (domainHit) return local + "@" + domainHit;

    var lastDot = domain.lastIndexOf(".");
    var sld = domain.slice(0, lastDot);
    var tld = domain.slice(lastDot + 1);
    var tldValid = POPULAR_TLDS.indexOf(tld) !== -1;

    // 2) Known second-level domain with a broken TLD (gmail.con, gmail.cm),
    //    or a broken SLD that's one edit from a known one.
    var sldHit =
      POPULAR_SLDS.indexOf(sld) !== -1 ? sld : closest(sld, POPULAR_SLDS, 1);
    var tldHit = tldValid ? tld : closest(tld, POPULAR_TLDS, 2);
    if (sldHit && tldHit && (sldHit !== sld || tldHit !== tld)) {
      return local + "@" + sldHit + "." + tldHit;
    }

    // 3) Domain-agnostic trailing-junk trim on an otherwise-valid TLD —
    //    works for any SLD, known or not: lufkinisd.orgj -> lufkinisd.org,
    //    anyschool.coms -> anyschool.com. Only fires when a valid TLD is a
    //    prefix of the typed one and at most 2 stray chars trail it.
    if (!tldValid) {
      for (var t = 0; t < POPULAR_TLDS.length; t++) {
        var base = POPULAR_TLDS[t];
        if (
          tld.length > base.length &&
          tld.length - base.length <= 2 &&
          tld.slice(0, base.length) === base
        ) {
          return local + "@" + sld + "." + base;
        }
      }
    }
    return null;
  }

  /**
   * Validate stripped (digits-only) phone against the selected country's
   * expected NSN length. Klaviyo silently drops profiles with malformed
   * phone_number — this guard keeps SMS opt-ins from disappearing.
   */
  function isValidPhone(localDigits, country) {
    if (!localDigits) return false;
    if (country === "US" || country === "CA") {
      return /^[2-9]\d{2}[2-9]\d{6}$/.test(localDigits);
    }
    if (country === "MX") {
      return /^\d{10}$/.test(localDigits);
    }
    return /^\d{7,14}$/.test(localDigits);
  }

  /**
   * Strip a redundant leading country code from a digits-only phone.
   * "+1 (555) 123-4567" typed/pasted/autofilled strips to 11 digits
   * starting with 1 — a US/CA NSN can never start with 1 (area codes
   * are 2-9), so dropping the extra leading digit is unambiguous.
   */
  function normalizeNsn(digits, country) {
    if (
      (country === "US" || country === "CA") &&
      digits.length === 11 &&
      digits[0] === "1"
    ) {
      return digits.slice(1);
    }
    return digits;
  }

  /**
   * Wires up the country pill + dropdown + tel input markup rendered by
   * snippets/lead-capture-phone-field.liquid. Returns an object with the
   * current selection ({ country, dial }) and a getDigits() helper that
   * returns the digits-only national number.
   *
   *   const phone = LeadCapture.bindPhoneCountry({
   *     wrap, trigger, menu, flagEl, dialEl, input,
   *     defaultCountry: 'US',
   *     onChange: (sel) => { … },
   *   });
   *
   * The caller owns submit handling — this module just keeps the UI
   * + selection state in sync.
   */
  function bindPhoneCountry(opts) {
    const { wrap, trigger, menu, flagEl, dialEl, input } = opts;
    if (!wrap || !trigger || !menu || !flagEl || !dialEl || !input) {
      console.warn("[lead-capture] bindPhoneCountry missing required element");
      return null;
    }

    const state = {
      country: opts.defaultCountry || "US",
      dial:
        menu.querySelector(
          `[data-lc-phone-option][data-country="${opts.defaultCountry || "US"}"]`,
        )?.getAttribute("data-dial") || "+1",
    };

    const setOpen = (open) => {
      menu.hidden = !open;
      trigger.setAttribute("aria-expanded", open ? "true" : "false");
    };

    const selectOption = (option) => {
      const country = option.getAttribute("data-country");
      const dial = option.getAttribute("data-dial");
      const flag = option.getAttribute("data-flag");
      if (!country || !dial || !flag) return;
      state.country = country;
      state.dial = dial;
      flagEl.textContent = flag;
      dialEl.textContent = dial;
      menu.querySelectorAll("[data-lc-phone-option]").forEach((opt) => {
        opt.setAttribute("aria-selected", opt === option ? "true" : "false");
      });
      setOpen(false);
      if (typeof opts.onChange === "function") opts.onChange({ ...state });
    };

    trigger.addEventListener("click", (e) => {
      e.stopPropagation();
      setOpen(menu.hidden);
    });

    menu.addEventListener("click", (e) => {
      const opt = e.target.closest("[data-lc-phone-option]");
      if (!opt) return;
      selectOption(opt);
    });

    document.addEventListener("click", (e) => {
      if (menu.hidden) return;
      if (!wrap.contains(e.target)) setOpen(false);
    });

    document.addEventListener("keydown", (e) => {
      if (!menu.hidden && e.key === "Escape") {
        setOpen(false);
        trigger.focus();
      }
    });

    // Strip non-digits as the user types — dial code lives on the pill.
    input.addEventListener("input", () => {
      const cleaned = (input.value || "").replace(/\D/g, "");
      if (cleaned !== input.value) input.value = cleaned;
    });

    // Mark default option for screen readers.
    const defaultOption = menu.querySelector(
      `[data-lc-phone-option][data-country="${state.country}"]`,
    );
    if (defaultOption) defaultOption.setAttribute("aria-selected", "true");

    return {
      get country() {
        return state.country;
      },
      get dial() {
        return state.dial;
      },
      getDigits() {
        return normalizeNsn(
          (input.value || "").replace(/\D/g, ""),
          state.country,
        );
      },
      getE164() {
        const digits = normalizeNsn(
          (input.value || "").replace(/\D/g, ""),
          state.country,
        );
        return digits ? `${state.dial}${digits}` : "";
      },
    };
  }

  /**
   * Look up the user's stored email/phone by user_id. Returns
   * { email, phone } (either may be empty/missing) on 200, or null
   * on any error. Never throws — autofill is a polish, not a gate.
   */
  async function fetchIdentity(userId, apiBase) {
    if (!userId || !apiBase) return null;
    try {
      const res = await fetch(
        `${apiBase}/api/users/get?user_id=${encodeURIComponent(userId)}`,
      );
      if (!res.ok) return null;
      const data = await res.json().catch(() => null);
      if (!data || typeof data !== "object") return null;
      // Raw values are passed through only when they parse as a real
      // address/number — once /api/users/get returns masked display forms
      // (backend#64), a masked string must never flow into a form submit.
      // Consumers that only need existence should read hasEmail/hasPhone.
      const rawEmail = typeof data.email === "string" ? data.email : "";
      const rawPhone = typeof data.phone === "string" ? data.phone : "";
      return {
        email: rawEmail && !/[*]/.test(rawEmail) && isValidEmail(rawEmail) ? rawEmail : "",
        phone: rawPhone && !/[*]/.test(rawPhone) ? rawPhone : "",
        hasEmail: !!(data.has_email || rawEmail),
        hasPhone: !!(data.has_phone || rawPhone),
        emailMasked: typeof data.email_masked === "string" ? data.email_masked : "",
        phoneMasked: typeof data.phone_masked === "string" ? data.phone_masked : "",
      };
    } catch (e) {
      return null;
    }
  }

  window.LeadCapture = {
    isValidEmail,
    suggestEmail,
    isValidPhone,
    normalizeNsn,
    bindPhoneCountry,
    fetchIdentity,
  };
})();
